import { Box, Paper, Stack, Typography } from '@mui/material';
import type { CompetitionPublic, OverallRanking, Result } from '../../types/results';
import type { Category, Discipline, Team } from '../../types/domain';
import { MEDAL_COLORS, QrPanel, buildBoardRows, type BoardRow } from './BoardShared';

interface MatrixBoardProps {
  competition: CompetitionPublic;
  results: Result[];
  overall: OverallRanking[];
  allTeams: Team[] | undefined;
  shortCode: string;
  publicUrl: string;
}

export const MatrixBoard = ({ competition, results, overall, allTeams, shortCode, publicUrl }: MatrixBoardProps) => {
  const showOverall = competition.overallRankingVisible && competition.publicRotateOverall;
  const totalCells =
    competition.categories.reduce((acc, c) => acc + c.disciplines.length, 0) +
    (showOverall ? overall.length : 0);
  // Symetricky grid: pro max 4 bunky vzdy 2 sloupce (2×2), pro 5–9 tri sloupce, jinak 4.
  const cols = totalCells <= 4 ? 2 : totalCells <= 9 ? 3 : 4;
  return (
    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="stretch" sx={{ flex: 1, minHeight: 0 }}>
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: `repeat(${Math.min(cols, 2)}, 1fr)`,
              lg: `repeat(${cols}, 1fr)`,
            },
            gridAutoRows: '1fr',
            gap: 1.5,
          }}
        >
          {competition.categories.flatMap((cat) =>
            cat.disciplines.map((disc) => (
              <MatrixCell
                key={`${cat.id}-${disc.id}`}
                category={cat}
                discipline={disc}
                results={results.filter((r) => r.disciplineId === disc.id)}
                allTeams={allTeams}
                showAll={!!competition.boardShowAllTeams}
                sortMode={competition.boardSortMode}
              />
            )),
          )}
          {showOverall &&
            overall.map((o) => {
              const category = competition.categories.find((c) => c.id === o.categoryId);
              if (!category) return null;
              return <OverallCell key={`overall-${o.categoryId}`} category={category} ranking={o} />;
            })}
        </Box>
        {competition.categories.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Soutěž zatím nemá nastavené kategorie.</Typography>
          </Paper>
        )}
      </Box>
      <QrPanel shortCode={shortCode} publicUrl={publicUrl} size="lg" />
    </Stack>
  );
};

const MatrixCell = ({
  category,
  discipline,
  results,
  allTeams,
  showAll,
  sortMode,
}: {
  category: Category;
  discipline: Discipline;
  results: Result[];
  allTeams: Team[] | undefined;
  showAll: boolean;
  sortMode: CompetitionPublic['boardSortMode'];
}) => {
  const rows: BoardRow[] = buildBoardRows(results, allTeams, category.id, discipline.id, {
    showAll,
    sortMode,
    topN: 3,
  });
  return (
    <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', minHeight: 180 }}>
      <Typography
        variant="overline"
        sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: '0.08em', lineHeight: 1 }}
      >
        {category.name}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1, lineHeight: 1.1 }}>
        {discipline.name}
      </Typography>
      {rows.length === 0 ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.disabled">
            zatím žádný výsledek
          </Typography>
        </Box>
      ) : (
        <Stack spacing={0.5}>
          {rows.map((r) => {
            const medal = r.rank != null ? MEDAL_COLORS[r.rank] : null;
            const leftLabel = sortMode === 'START_NUMBER' ? `#${r.teamStartNumber}` : (r.rank ?? '–');
            return (
              <Box
                key={r.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '36px 1fr auto',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  py: 0.5,
                  bgcolor: medal ? `${medal.bg}33` : 'transparent',
                  borderLeft: medal ? `4px solid ${medal.bg}` : '4px solid transparent',
                  borderRadius: 0.5,
                  opacity: r.__missing ? 0.55 : 1,
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', fontVariantNumeric: 'tabular-nums' }}>
                  {leftLabel}
                </Typography>
                <Typography sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.teamName}
                </Typography>
                <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1rem' }}>
                  {r.isNp ? 'NP' : r.timeFinalFormatted}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};

const OverallCell = ({ category, ranking }: { category: Category; ranking: OverallRanking }) => {
  const topThree = [...ranking.teams].sort((a, b) => a.finalRank - b.finalRank).slice(0, 3);
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 180,
        borderColor: 'primary.main',
        borderWidth: 2,
        bgcolor: 'action.hover',
      }}
    >
      <Typography
        variant="overline"
        sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: '0.08em', lineHeight: 1 }}
      >
        {category.name}
      </Typography>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 1, lineHeight: 1.1, color: 'primary.main' }}>
        Celkové pořadí
      </Typography>
      {topThree.length === 0 ? (
        <Typography variant="body2" color="text.disabled">
          žádní účastníci
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {topThree.map((t) => {
            const medal = MEDAL_COLORS[t.finalRank];
            return (
              <Box
                key={t.teamId}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '28px 1fr auto',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  py: 0.5,
                  bgcolor: medal ? `${medal.bg}33` : 'transparent',
                  borderLeft: medal ? `4px solid ${medal.bg}` : '4px solid transparent',
                  borderRadius: 0.5,
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', fontVariantNumeric: 'tabular-nums' }}>
                  {t.finalRank}
                </Typography>
                <Typography sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.teamName}
                </Typography>
                <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1rem' }}>
                  {t.totalPoints} b.
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};
